import { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../contexts/auth-context';
import { ThemeContext } from '../../components/molecules/Nav';
import { getQueuesAPI } from '../../util.js';
import { getMostRecentSeriesAndGame } from '../Retail/helper/helper_functions.js';
import moment from 'moment';

export function useBallparkController() {
  const authCtx = useContext(AuthContext);
  const [theme] = useContext(ThemeContext);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [ballparkAccess, setBallparkAccess] = useState({
    venueEnabled: null,
    locationCount: 0,
    userBallparkEnabled: null,
    userBallparkEditEnabled: null,
    loading: true,
  });
  
  const [viewMode, setViewMode] = useState('overview');
  
  const [now] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentSeason, setCurrentSeason] = useState(moment().format('YYYY'));
  const [currentSeries, setCurrentSeries] = useState(null);
  const [currentGame, setCurrentGame] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState({ option: 'count', direction: 'desc' });
  
  const [overallMetrics, setOverallMetrics] = useState({
    totalSessions: 0,
    totalSearches: 0,
    avgSearchesPerSession: 0,
    uniqueUsers: 0,
    avgSessionDuration: 0,
    avgSessionDurationFormatted: '0:00',
    medianSessionDuration: 0,
    medianSessionDurationFormatted: '0:00',
  });
  
  const [gameMetrics, setGameMetrics] = useState({
    totalSessions: 0,
    totalSearches: 0,
    avgSearchesPerSession: 0,
    uniqueUsers: 0,
    avgSessionDuration: 0,
    avgSessionDurationFormatted: '0:00',
  });
  
  const [topSearchTerms, setTopSearchTerms] = useState([]);
  const [sessionsOverTime, setSessionsOverTime] = useState([]);
  const [searchDistribution, setSearchDistribution] = useState([]);
  const [searchTermsOverTime, setSearchTermsOverTime] = useState([]);
  const [searchTypeBreakdown, setSearchTypeBreakdown] = useState({});
  const [sessionDurationDistribution, setSessionDurationDistribution] = useState([]);
  const [noResultsSearches, setNoResultsSearches] = useState([]);
  const [searchClusters, setSearchClusters] = useState([]);
  const [demandByCategory, setDemandByCategory] = useState([]);
  const [predictedSurges, setPredictedSurges] = useState([]);
  const [staffingRecommendations, setStaffingRecommendations] = useState([]);
  const [searchJourneys, setSearchJourneys] = useState([]);
  const [userTypeBreakdown, setUserTypeBreakdown] = useState({});
  const [sectionHeatmap, setSectionHeatmap] = useState([]);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [alertHistory, setAlertHistory] = useState([]);
  const [menuInsights, setMenuInsights] = useState([]);
  const [timeBasedRecommendations, setTimeBasedRecommendations] = useState([]);
  const [games, setGames] = useState([]);
  const [ballparkLocations, setBallparkLocations] = useState([]);

  const isToday = moment(selectedDate).isSame(moment(), 'day');

  // Clear shared chart state to prevent cross-view bleed
  const clearSharedState = useCallback(() => {
    setSearchTypeBreakdown({});
    setTopSearchTerms([]);
    setSessionsOverTime([]);
    setSearchDistribution([]);
    setSessionDurationDistribution([]);
    setSearchTermsOverTime([]);
    setNoResultsSearches([]);
    setSearchJourneys([]);
    setUserTypeBreakdown({});
    setActiveAlerts([]);
    setAlertHistory([]);
    setPredictedSurges([]);
    setStaffingRecommendations([]);
    setDemandByCategory([]);
    setSectionHeatmap([]);
  }, []);

  // =============================================
  // INITIAL DATA LOADING
  // =============================================

  // 1. Check ballpark access on mount
  useEffect(() => {
    checkBallparkAccess();
  }, []);

  // 2. Fetch ballpark locations once access is confirmed
  useEffect(() => {
    if (ballparkAccess.venueEnabled && ballparkAccess.userBallparkEnabled) {
      fetchBallparkLocations();
    }
  }, [ballparkAccess.venueEnabled, ballparkAccess.userBallparkEnabled]);

  // 3. Fetch games and overview on access confirmed / season change
  useEffect(() => {
    if (ballparkAccess.venueEnabled && ballparkAccess.userBallparkEnabled) {
      fetchGames();
      fetchOverallStats();
    }
  }, [currentSeason, ballparkAccess.venueEnabled, ballparkAccess.userBallparkEnabled]);

  // 4. Fetch game stats when game or view changes
  useEffect(() => {
    if (currentGame && viewMode === 'game' && ballparkAccess.venueEnabled && ballparkAccess.userBallparkEnabled) {
      fetchGameStats();
    }
  }, [currentGame, viewMode, ballparkAccess.venueEnabled, ballparkAccess.userBallparkEnabled]);

  // 5. Sync selectedDate when game changes so isToday computes correctly
  useEffect(() => {
    if (currentGame) {
      setSelectedDate(moment(currentGame.date).toDate());
    }
  }, [currentGame]);

  // =============================================
  // POLLING
  // =============================================
  // 
  // How polling works:
  //
  // setInterval runs a callback every N milliseconds (here 60000 = 60s).
  // The useEffect sets up the interval when the component mounts or when
  // its dependencies change (viewMode, currentGame, access flags).
  //
  // When any dependency changes, the cleanup function runs first
  // (clearInterval), then the effect re-runs and creates a NEW interval
  // with the current values of viewMode/currentGame. This means:
  //
  // - Switch from overview to game → old interval cleared, new interval
  //   created that calls fetchGameStats every 60s
  // - Switch from game to overview → old interval cleared, new interval
  //   created that calls fetchOverallStats every 60s
  // - Switch to ballpark tab → old interval cleared, new interval created
  //   but neither condition matches so it just ticks doing nothing
  // - Component unmounts → cleanup runs, interval cleared, no leaks
  //
  // The interval callback checks viewMode at execution time to decide
  // which fetch to call. Since the interval is recreated on dependency
  // change, it always has the correct current references.
  //
  // This runs regardless of whether today is a game day — the overview
  // aggregates the full season so new sessions always matter, and the
  // game day view needs live updates during active games.
  //
  useEffect(() => {
    let interval;
    if (ballparkAccess.venueEnabled && ballparkAccess.userBallparkEnabled) {
      interval = setInterval(() => {
        if (viewMode === 'game' && currentGame) {
          fetchGameStats();
        } else if (viewMode === 'overview') {
          fetchOverallStats();
        }
      }, 60000);
    }
    return () => clearInterval(interval);
  }, [viewMode, currentGame, ballparkAccess.venueEnabled, ballparkAccess.userBallparkEnabled]);

  // =============================================
  // VIEW MODE SWITCHING
  // =============================================

  const handleViewModeChange = useCallback((newMode) => {
    setViewMode(newMode);
    clearSharedState();

    if (newMode === 'overview') {
      fetchOverallStats();
    } else if (newMode === 'game' && currentGame) {
      fetchGameStats();
    }
  }, [currentGame, clearSharedState]);

  // =============================================
  // API CALLS
  // =============================================

  const checkBallparkAccess = () => {
    getQueuesAPI(
      "ballpark-permissions-check",
      { venue: authCtx.venue },
      (data, error) => {
        if (error) {
          if (error.status === 401) {
            authCtx.logout();
            navigate("/auth");
          }
          console.error('Failed to check ballpark access:', error);
          setBallparkAccess({
            venueEnabled: false,
            locationCount: 0,
            userBallparkEnabled: false,
            userBallparkEditEnabled: false,
            loading: false,
          });
        } else {
          setBallparkAccess({
            venueEnabled: data.venueEnabled,
            locationCount: data.locationCount,
            userBallparkEnabled: data.userBallparkEnabled,
            userBallparkEditEnabled: data.userBallparkEditEnabled,
            loading: false,
          });
        }
      },
      authCtx.token
    );
  };

  const toggleFeatured = (locationId, itemId, itemType, featured, onComplete) => {
    getQueuesAPI(
      "ballpark-toggle-featured",
      {
        venue: authCtx.venue,
        locationId,
        itemId: itemId || null,
        itemType: itemType || null,
        featured,
      },
      (data, error) => {
        if (onComplete) onComplete(!error);
        if (error) console.error('Failed to toggle featured:', error);
      },
      authCtx.token
    );
  };

  const updateTags = (locationId, itemId, itemType, tags, onComplete) => {
    getQueuesAPI(
      "ballpark-update-tags",
      {
        venue: authCtx.venue,
        locationId,
        itemId: itemId || null,
        itemType: itemType || null,
        tags,
      },
      (data, error) => {
        if (onComplete) onComplete(!error);
        if (error) console.error('Failed to update tags:', error);
      },
      authCtx.token
    );
  };

  const updateDescription = (locationId, itemId, itemType, description, onComplete) => {
  getQueuesAPI(
    "ballpark-update-description",
    {
      venue: authCtx.venue,
      locationId,
      itemId: itemId || null,
      itemType: itemType || null,
      description,
    },
    (data, error) => {
      if (onComplete) onComplete(!error);
      if (error) console.error('Failed to update description:', error);
    },
    authCtx.token
  );
};

  const fetchBallparkLocations = () => {
    getQueuesAPI(
      "ballpark-locations",
      { venue: authCtx.venue },
      (data, error) => {
        if (error) {
          if (error.status === 401) {
            authCtx.logout();
            navigate("/auth");
          }
          console.error('Failed to fetch ballpark locations:', error);
        } else {
          setBallparkLocations(data.locations || []);
        }
        setLoading(false);
      },
      authCtx.token
    );
  };

  const fetchGames = () => {
    getQueuesAPI(
      "ballpark-schedule",
      {
        venue: authCtx.venue,
      },
      (data_, error) => {
        if (error) {
          if (error.status === 401) {
            authCtx.logout();
            navigate("/auth");
          }
          console.error('Failed to fetch games:', error);
          return;
        }

        const sortedGames = [...data_].sort((a, b) =>
          moment(b.dates[0].date).valueOf() - moment(a.dates[0].date).valueOf()
        );
        const mostRecentYear = moment(sortedGames[0]?.dates[0]?.date).format("YYYY");

        if (!currentSeason || currentSeason === moment().format('YYYY')) {
          const adjustedNow = moment(now).subtract(3, 'hours').toDate();
          const { recentSeries, recentGame } = getMostRecentSeriesAndGame(data_, adjustedNow);

          if (recentGame) {
            const recentGameYear = moment(recentGame.date).format("YYYY");
            setCurrentSeason(recentGameYear);
          } else {
            setCurrentSeason(mostRecentYear);
          }
          setCurrentSeries(recentSeries);
          setCurrentGame(recentGame);
        }

        setGames(data_);
      },
      authCtx.token
    );
  };

  const fetchOverallStats = () => {
    getQueuesAPI(
      "ballpark-season-overview",
      {
        venue: authCtx.venue,
        season: currentSeason,
      },
      (data, error) => {
        if (error) {
          console.error('Failed to fetch season overview:', error);
          return;
        }
        setOverallMetrics({
          totalSessions: data.totalSessions || 0,
          totalSearches: data.totalSearches || 0,
          avgSearchesPerSession: data.avgSearchesPerSession || 0,
          uniqueUsers: data.uniqueUsers || 0,
          avgSessionDuration: data.avgSessionDuration || 0,
          avgSessionDurationFormatted: data.avgSessionDurationFormatted || '0:00',
          medianSessionDuration: data.medianSessionDuration || 0,
          medianSessionDurationFormatted: data.medianSessionDurationFormatted || '0:00',
        });
        setSearchTypeBreakdown(data.searchTypeBreakdown || {});
        setNoResultsSearches(data.noResultsSearches || []);
        setTopSearchTerms(data.topSearchTerms || []);
        setSessionsOverTime(data.sessionsOverTime || []);
        setSearchDistribution(data.searchDistribution || []);
        setSessionDurationDistribution(data.sessionDurationDistribution || []);
        setSearchJourneys(data.searchJourneys || []);
        setUserTypeBreakdown(data.userTypeBreakdown || {});
      },
      authCtx.token
    );
  };

  const fetchGameStats = () => {
    if (!currentGame?.date) return;

    const dateStr = moment(currentGame.date).format('YYYY-MM-DD');

    getQueuesAPI(
      "ballpark-game-day",
      {
        venue: authCtx.venue,
        date: dateStr,
      },
      (data, error) => {
        if (error) {
          if (error.status === 401) {
            authCtx.logout();
            navigate("/auth");
          }
          console.error('Failed to fetch game stats:', error);
          return;
        }

        setGameMetrics({
          totalSessions: data.totalSessions || 0,
          totalSearches: data.totalSearches || 0,
          avgSearchesPerSession: data.avgSearchesPerSession || 0,
          uniqueUsers: data.uniqueUsers || 0,
          avgSessionDuration: data.avgSessionDuration || 0,
          avgSessionDurationFormatted: data.avgSessionDurationFormatted || '0:00',
        });
        setSearchTypeBreakdown(data.searchTypeBreakdown || {});
        setSessionDurationDistribution(data.sessionDurationDistribution || []);
        setTopSearchTerms(data.topSearchTerms || []);
        setSessionsOverTime(data.sessionsOverTime || []);
        setSearchDistribution(data.searchDistribution || []);
        setSearchTermsOverTime(data.searchTermsOverTime || []);
        setNoResultsSearches(data.noResultsSearches || []);

        // Placeholder for future live features — will read from API when implemented
        setActiveAlerts(data.activeAlerts || []);
        setAlertHistory(data.alertHistory || []);
        setPredictedSurges(data.predictedSurges || []);
        setStaffingRecommendations(data.staffingRecommendations || []);
        setDemandByCategory(data.demandByCategory || []);
        setSectionHeatmap(data.sectionHeatmap || []);
      },
      authCtx.token
    );
  };

  // =============================================
  // UI HELPERS
  // =============================================

  const dismissAlert = useCallback((alertId) => {
    setActiveAlerts(prev => prev.filter(a => a.id !== alertId));
  }, []);

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleSort = (option, direction) => {
    setSortOption({ option, direction });
  };

  const getFilteredSearchTerms = () => {
    let filtered = [...(topSearchTerms || [])];
    
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.term.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    filtered.sort((a, b) => {
      const multiplier = sortOption.direction === 'asc' ? 1 : -1;
      if (sortOption.option === 'name') {
        return multiplier * a.term.localeCompare(b.term);
      }
      return multiplier * (a.count - b.count);
    });
    
    return filtered;
  };

  // =============================================
  // RETURN
  // =============================================

  return {
    authCtx,
    theme,
    loading,
    error,
    ballparkAccess,
    viewMode,
    setViewMode: handleViewModeChange,
    now,
    selectedDate,
    setSelectedDate,
    currentSeason,
    setCurrentSeason,
    currentSeries,
    setCurrentSeries,
    currentGame,
    setCurrentGame,
    games,
    isToday,
    searchTerm,
    handleSearch,
    sortOption,
    handleSort,
    overallMetrics,
    gameMetrics,
    metrics: viewMode === 'overview' ? overallMetrics : gameMetrics,
    topSearchTerms: getFilteredSearchTerms(),
    rawTopSearchTerms: topSearchTerms,
    sessionsOverTime,
    searchDistribution,
    searchTermsOverTime,
    searchTypeBreakdown,
    sessionDurationDistribution,
    noResultsSearches,
    searchClusters,
    demandByCategory,
    predictedSurges,
    staffingRecommendations,
    searchJourneys,
    userTypeBreakdown,
    sectionHeatmap,
    activeAlerts,
    alertHistory,
    dismissAlert,
    menuInsights,
    timeBasedRecommendations,
    ballparkLocations,
    toggleFeatured,
    updateTags,
    currentLocation: {
      league: authCtx.league,
      venue: authCtx.venue,
    },
    updateDescription,
  };
}